import { PopoverButton } from '@headlessui/react';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { Link } from '@remix-run/react';
import { GQLBrandFragment, GQLCategoryFragment, GQLProductStatusType, GQLSearchProductFragment } from '@theprintbar/graphql';
import { WebRoutes } from '@theprintbar/shared';
import { ButtonSize, PrimaryLinkButton, ProductTile } from '@theprintbar/ui';
import React from 'react';
import { Configure, Hits, Index, useInstantSearch } from 'react-instantsearch';
import { RootLoaderData } from '~/root';

interface SearchResultsProps {
	setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
	data: RootLoaderData;
	close: () => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({ data, close, setSearchTerm }) => {
	const { indexUiState, results } = useInstantSearch();

	const productBrands =
		results?.hits?.filter((hit: GQLSearchProductFragment) => hit.brand).map((hit: GQLSearchProductFragment) => hit.brand) || []; // If no brands are found by keyword match, display the brands from the product results

	const uniqueProductBrands = Array.from(new Map(productBrands.filter(brand => brand?.name).map(brand => [brand?.name, brand])).values()); // Remove duplicate brands

	return (
		<div className="h-full max-h-[75vh] lg:max-h-[60vh] flex flex-col lg:flex-row gap-4 divide-y lg:divide-y-0 lg:divide-x divide-blue">
			<div className="lg:space-y-4 grid grid-cols-2 lg:grid-cols-1 h-min pr-0 lg:pr-4">
				{/* Brand */}
				<div>
					<p className="font-belfast text-xs uppercase font-bold mb-2">Brands</p>
					<Index indexName={data.algoliaIndexes.brands}>
						<Configure hitsPerPage={4} />
						<NoResultsBoundary
							fallback={
								<div className="space-y-2 flex flex-col">
									{uniqueProductBrands?.map(brand => (
										<PopoverButton
											as={Link}
											className="py-1 font-belfast text-xs lg:text-sm"
											to={`${WebRoutes.Products()}?brand="${brand?.code}"`}
											key={brand?.id}
										>
											{brand?.name}
										</PopoverButton>
									))}
								</div>
							}
						>
							<Hits<GQLBrandFragment>
								hitComponent={({ hit }) => {
									return hit ? (
										<PopoverButton
											as={Link}
											className="py-1 font-belfast text-xs lg:text-sm"
											to={`${WebRoutes.Products()}?brand="${hit.code}"`}
										>
											{hit.name}
										</PopoverButton>
									) : null;
								}}
								classNames={{
									list: 'space-y-2',
								}}
							/>
						</NoResultsBoundary>
					</Index>
				</div>
				{/* Category */}
				<div>
					<p className="font-belfast text-xs uppercase font-bold mb-2 whitespace-nowrap">Categories</p>
					<Index indexName={data.algoliaIndexes.categories}>
						<Configure hitsPerPage={4} />
						<NoResultsBoundary fallback={<></>}>
							<Hits<GQLCategoryFragment>
								hitComponent={({ hit }) => {
									return hit ? (
										<PopoverButton
											as={Link}
											to={WebRoutes.Category(hit.slug)}
											className="py-1 font-belfast text-xs lg:text-sm"
										>
											{hit.name}
										</PopoverButton>
									) : null;
								}}
								classNames={{
									list: 'space-y-2',
								}}
							/>
						</NoResultsBoundary>
					</Index>
				</div>
			</div>
			<div className="w-full overflow-y-auto py-4 lg:px-4 lg:py-0">
				<Index indexName={data.algoliaIndexes.products}>
					<Configure hitsPerPage={12} />
					<NoResultsBoundary
						fallback={
							<div className="">
								<h5 className="text-lg mb-2">No Results Found</h5>
								<p>Please try a different search query</p>
							</div>
						}
					>
						<div>
							<p className="font-belfast text-xs uppercase font-bold mb-2 whitespace-nowrap">
								{`${results?.nbHits} Result${results?.nbHits === 1 ? '' : 's'} Found`}
							</p>
						</div>
						<Hits<GQLSearchProductFragment>
							classNames={{
								root: '',
								list: 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 3xl:grid-cols-8 gap-4 p-4',
							}}
							hitComponent={({ hit }) => {
								return hit.status === GQLProductStatusType.Published ? (
									<ProductTile
										reloadDocument
										onClick={close}
										productPath={WebRoutes.Product(hit.slug)}
										product={hit}
										key={hit.id}
									/>
								) : null;
							}}
						/>
						<PopoverButton
							as={PrimaryLinkButton}
							to={`${WebRoutes.Products()}${indexUiState.query ? `?search="${indexUiState.query}"` : ''}`}
							icon={ArrowUpRightIcon}
							size={ButtonSize.L}
							className="absolute bottom-8 left-1/2 -translate-x-1/2 px-4 py-2 uppercase font-belfast !font-bold text-xs"
						>
							View All {results?.nbHits} Products
						</PopoverButton>
					</NoResultsBoundary>
				</Index>
			</div>
		</div>
	);
};

export default SearchResults;

function NoResultsBoundary({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
	const { results } = useInstantSearch();
	// The `__isArtificial` flag makes sure not to display the No Results message
	// when no hits have been returned.
	if (!results.__isArtificial && results.nbHits === 0) {
		return (
			<>
				{fallback}
				<div hidden>{children}</div>
			</>
		);
	}

	return children;
}
